import type { ActivityConfig, ProcedureObservationConfig } from "@repo/api";
import { z } from "zod";
import type { ProcedureFormState } from "@/features/visits/components/charting/procedure-observation-field";
import type { ProcedureFieldValues } from "@/features/visits/forms/use-procedure-save";
import type { PresentedOrderSetDto } from "@/lib/order-set-presentation";

// === Schema & Types ===

export const procedureFormSchema = z.object({
  obsStrings: z.record(z.string(), z.string()),
  obsBooleans: z.record(z.string(), z.boolean()),
  compStrings: z.record(z.string(), z.string()),
  compBooleans: z.record(z.string(), z.boolean()),
  procNote: z.string(),
});

export type ProcedureFormValues = z.infer<typeof procedureFormSchema>;

export const EMPTY_PROCEDURE_FORM_VALUES: ProcedureFormValues = {
  obsStrings: {},
  obsBooleans: {},
  compStrings: {},
  compBooleans: {},
  procNote: "",
};

/** Delimiter for component keys: `obsCode__fieldKey` */
export function compKey(obsCode: string, fieldKey: string): string {
  return `${obsCode}__${fieldKey}`;
}

// === Defaults Builder ===

type TreatmentObservation = NonNullable<
  PresentedOrderSetDto["orders"][number]["treatmentProcedure"]
>["observations"][number];

function extractComponentValue(
  comp: TreatmentObservation["components"][number],
): string | number | boolean | undefined {
  if (comp.value.kind === "Choice") return comp.value.selected[0] ?? comp.value.freeText;
  return comp.value.value;
}

function fieldIsBool(obsConfig: ProcedureObservationConfig, fieldKey?: string): boolean {
  if (fieldKey) {
    const comp = obsConfig.components?.find((c) => c.key === fieldKey);
    return comp?.type === "boolean";
  }
  return obsConfig.field?.type === "boolean";
}

/** Maps observation code → FHIR resource ID for existing observations */
export type ObservationIdMap = Record<string, string>;

export interface BuildDefaultsResult {
  values: ProcedureFormValues;
  observationIds: ObservationIdMap;
}

export function buildDefaults(
  config: ActivityConfig,
  existingObs: readonly TreatmentObservation[],
): BuildDefaultsResult {
  const obsStrings: Record<string, string> = {};
  const obsBooleans: Record<string, boolean> = {};
  const compStrings: Record<string, string> = {};
  const compBooleans: Record<string, boolean> = {};
  const observationIds: ObservationIdMap = {};

  for (const obsConfig of config.observations) {
    const existing = existingObs.find(
      (observation) =>
        observation.definitionId === obsConfig.definitionId && observation.lifecycle.status !== "EnteredInError",
    );
    if (!existing) continue;

    observationIds[obsConfig.code] = existing.observationId;

    if (obsConfig.multiComponent) {
      for (const comp of existing.components) {
        const val = extractComponentValue(comp);
        if (val === undefined) continue;
        const compConfig = obsConfig.components?.find((candidate) => candidate.componentId === comp.componentId);
        if (!compConfig) continue;

        const k = compKey(obsConfig.code, compConfig.key);
        if (compConfig.type === "boolean") {
          compBooleans[k] = val === true;
        } else {
          compStrings[k] = String(val);
        }
      }
    } else {
      const val = existing.components[0] ? extractComponentValue(existing.components[0]) : undefined;
      if (val === undefined) continue;
      if (fieldIsBool(obsConfig)) {
        obsBooleans[obsConfig.code] = val === true;
      } else {
        obsStrings[obsConfig.code] = String(val);
      }
    }
  }

  return {
    values: { obsStrings, obsBooleans, compStrings, compBooleans, procNote: "" },
    observationIds,
  };
}

// === Submit Converter ===

export function formValuesToSaveArgs(
  config: ActivityConfig,
  values: ProcedureFormValues,
): {
  obsState: ProcedureFormState;
  procState: ProcedureFieldValues;
} {
  const obsState: ProcedureFormState = {};

  for (const obsConfig of config.observations) {
    if (obsConfig.multiComponent && obsConfig.components) {
      const components: Record<string, Record<string, string | number | boolean | undefined>> = {};
      for (const comp of obsConfig.components) {
        const k = compKey(obsConfig.code, comp.key);
        let val: string | number | boolean | undefined;
        if (comp.type === "boolean") {
          val = values.compBooleans[k];
        } else {
          const str = values.compStrings[k];
          if (str === undefined || str === "") continue;
          const ft = comp.type as string;
          val = ft === "number" || ft === "integer" ? Number(str) : str;
        }
        if (!components[comp.key]) components[comp.key] = {};
        components[comp.key].value = val;
      }
      if (Object.keys(components).length > 0) {
        obsState[obsConfig.code] = { components };
      }
    } else {
      let val: string | number | boolean | undefined;
      if (fieldIsBool(obsConfig)) {
        val = values.obsBooleans[obsConfig.code];
      } else {
        const str = values.obsStrings[obsConfig.code];
        if (str === undefined || str === "") continue;
        const ft = obsConfig.field?.type;
        val = ft === "number" || ft === "integer" ? Number(str) : str;
      }
      obsState[obsConfig.code] = { value: val };
    }
  }

  const procState: ProcedureFieldValues = {
    note: values.procNote,
  };

  return { obsState, procState };
}

export { useAppForm, withForm } from "@/shared/forms/app-form";
